import { useRef, useState } from "react";
import { BarChart3, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { todayAthens } from "../../../lib/format";
import { useCategories, useCustomer, useInsights, useItems } from "../hooks";
import { DateField } from "../../../components/common/DateField";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";
import { MonthlyChart, ShareBars } from "./Charts";
import { Kpis, TopList } from "./Kpis";
import { exportPdf } from "./exportPdf";

export const seasonRange = (today) => {
  const y = today < `${today.slice(0, 4)}-04-01` ? Number(today.slice(0, 4)) - 1 : Number(today.slice(0, 4));
  const end = `${y}-10-31`;
  return { from: `${y}-04-01`, to: end < today ? end : today };
};

// purchase history is monthly, so periods snap to whole months
const last12 = (today) => {
  const d = new Date(`${today.slice(0, 7)}-01T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 11);
  return { from: d.toISOString().slice(0, 10), to: today };
};
const monthStart = (iso) => `${iso.slice(0, 7)}-01`;
const monthName = (iso, lang) => new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "el-GR", { month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${iso.slice(0, 7)}-15T12:00:00Z`));

const Card = ({ title, sub, children, testId }) => (
  <section className="min-w-0 rounded-2xl bg-white p-4 shadow-sm sm:p-5" data-testid={testId}>
    <h3 className="text-base font-semibold text-navy md:text-lg">{title}</h3>
    {sub && <p className="text-xs text-slate-500">{sub}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const PeriodPicker = ({ preset, setPreset, range, setRange }) => {
  const { t } = useI18n();
  const today = todayAthens();
  const chip = (on) => `h-10 rounded-full px-4 text-sm font-medium transition-colors ${on ? "bg-navy text-white" : "bg-white text-navy shadow-sm"}`;
  const pick = (k) => {
    setPreset(k);
    if (k === "season") setRange(seasonRange(today));
    if (k === "12") setRange(last12(today));
  };
  return (
    <div className="space-y-2" data-pdf-ignore="true">
      <div className="flex flex-wrap gap-2">
        {[["season", "insights.season"], ["12", "insights.last12"], ["custom", "insights.custom"]].map(([k, l]) => (
          <button key={k} type="button" className={chip(preset === k)} onClick={() => pick(k)} data-testid={`insights-period-${k}`}>{t(l)}</button>
        ))}
      </div>
      {preset === "custom" && (
        <div className="grid max-w-md grid-cols-2 gap-2">
          <label className="text-xs text-slate-500">{t("account.from")}<DateField value={range.from} onChange={(v) => v && setRange((r) => ({ ...r, from: monthStart(v) }))} testId="insights-from" /></label>
          <label className="text-xs text-slate-500">{t("account.to")}<DateField value={range.to} onChange={(v) => v && setRange((r) => ({ ...r, to: v }))} testId="insights-to" /></label>
        </div>
      )}
    </div>
  );
};

const Body = ({ d }) => {
  const { t, lang } = useI18n();
  const items = useItems();
  const cats = useCategories();
  const catName = (c) => (cats.data || []).find((x) => x.code === c)?.[`name_${lang}`] || c;
  const itemName = (c) => (items.data || []).find((x) => x.code === c)?.[`name_${lang}`] || c;
  const a = d.amounts_visible;
  if (items.isLoading || cats.isLoading) return <SkeletonBlock className="h-64" />;
  return (
    <div className="space-y-4">
      <Kpis d={d} />
      <Card title={t("insights.monthly")} sub={t("insights.monthlySub")} testId="insights-monthly"><MonthlyChart data={d.monthly} amounts={a} /></Card>
      {d.period.orders === 0 ? <EmptyState icon={BarChart3} title={t("insights.empty")} testId="insights-empty" /> : (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <Card title={t("insights.byCategory")} testId="insights-category"><ShareBars rows={d.by_category} amounts={a} label={catName} testId="bars-category" /></Card>
            <Card title={t("insights.byBrand")} testId="insights-brand"><ShareBars rows={d.by_brand} amounts={a} label={(b) => b} testId="bars-brand" /></Card>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {a && <Card title={t("insights.topValue")} testId="insights-top-value"><TopList rows={d.top_value} by="value" nameOf={itemName} testId="top-value-list" /></Card>}
            <Card title={t("insights.topQty")} testId="insights-top-qty"><TopList rows={d.top_qty} by="qty" nameOf={itemName} testId="top-qty-list" /></Card>
          </div>
        </>
      )}
    </div>
  );
};

export const InsightsPanel = ({ customerCode, extra }) => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const customer = useCustomer(customerCode || user.customer_code);
  const ref = useRef(null);
  const [preset, setPreset] = useState("12");
  const [range, setRange] = useState(() => last12(todayAthens()));
  const [busy, setBusy] = useState(false);
  const q = useInsights(range.from, range.to, customerCode);

  const onExport = async () => {
    setBusy(true);
    try { await exportPdf(ref.current, `vavlis-insights-${range.from}-${range.to}.pdf`); } catch (e) { toast.error(t("common.error")); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="insights-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PeriodPicker preset={preset} setPreset={setPreset} range={range} setRange={setRange} />
        <button type="button" onClick={onExport} disabled={busy || !q.data} data-testid="insights-export-pdf"
          className="flex h-11 items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-transform duration-150 active:scale-95 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {t(busy ? "insights.exporting" : "insights.export")}
        </button>
      </div>
      {range.from > range.to && <p className="text-sm text-rose-600">{t("insights.invalid")}</p>}
      <div ref={ref} className="space-y-4" data-testid="insights-report">
        <div>
          <p className="text-lg font-bold text-navy" data-testid="insights-heading">{t("insights.title")} · {customer.data?.name}</p>
          <p className="text-xs text-slate-500" data-testid="insights-range">{monthName(range.from, lang)} – {monthName(range.to, lang)} · {t("insights.wholeMonths")}</p>
          {q.data && !q.data.amounts_visible && <p className="mt-1 text-xs text-slate-500" data-testid="insights-qty-only">{t("insights.qtyOnly")}</p>}
        </div>
        {q.isLoading ? <div className="space-y-3"><SkeletonBlock className="h-24" /><SkeletonBlock className="h-64" /></div> : q.data && <Body d={q.data} />}
        {extra && extra(range)}
      </div>
    </div>
  );
};
